<template>
  <div class="position-relative h-100">

    <b-form
        novalidate
        ref="form"
        :validated="formValidated">
      <b-row class="no-gutters">

        <b-col :md="formColumns">

          <b-row
              v-for="(input, index) in fields"
              :key="`${index}${input.name}`"
              v-if="!!fields && showInput(input.name)"
              :class="[`mb-${mb}`]"
          >
            <b-col :md="labelBlockWith" v-if="input.type !== 'switch' && input.label">
              <label :for="input.name" :class="['pr-0', {'white-space-nowrap': !input.wrapLabel}]">
                {{ editing && input.label_editing ? input.label_editing : input.label}}
                <span class="text-danger" v-if="input.required">{{$t('form.required_star')}}</span>
              </label>
            </b-col>
            <b-col
                :class="[input.name === 'price' ? 'd-flex flex-row' : '']"
                class="position-relative">

              <multiselect
                  v-if="input.type === 'select' && selectOptions[input.name]"
                  :loading="isSelectLoading[input.name]"
                  v-model="params[input.name]"
                  :multiple="input.multiple"
                  :id="input.name"
                  :label="input.trackBy || 'name'"
                  :track-by="input.trackBy || 'name'"
                  :group-values="input.groupValues || null"
                  :group-label="input.groupLabel || null"
                  :group-select="false"
                  :preselect-first="input.preselectFirst"
                  :placeholder="input.placeholder"
                  :options="selectOptions[input.name]"
                  :searchable="input.searchable"
                  :taggable="input.taggable"
                  :allow-empty="input.allowEmpty"
                  :close-on-select="!input.multiple"
                  @open="id => {changeHighlightedOption(id)}"
                  @search-change="query => {debounceSearchSelectOption(input.options_url, input.name, input.filter_field, query)}"
                  @tag="value => {addTag(input.name, value)}"
                  @remove="value => {debounceCreateParamsObject(input.name, value)}"
                  @input="value => {debounceCreateParamsObject(input.name, value)}"
                  select-label=""
                  selected-label=""
                  deselect-label="X "
                  tag-placeholder="+">
                <template slot="noResult">
                  {{$t('global.no_results')}}
                </template>
              </multiselect>

              <b-input-group class="position-relative">

                <b-form-input
                    class="rounded"
                    v-if="input.type === 'text' ||( input.type === 'number' && input.name !== 'price'  && input.name !== 'quantity') || input.type === 'tel' || input.type === 'email' || input.type === 'url' || input.type === 'password'"
                    :readonly="input.readOnly"
                    :maxlength="input.maxLength"
                    v-model="params[input.name]"
                    :type="input.type === 'password' && inputPasswordVisible ? 'text' : input.type"
                    :placeholder="input.placeholder"
                    :name="input.name"
                    :id="`${input.name}-${index}`"
                    :min="input.min"
                    :required="input.required"
                    @input="value => {debounceCreateParamsObject(input.name, value)}"
                />

                <image-upload
                    v-if="input.type === 'file'"
                    v-model="params[input.name]"
                    :photos="params[input.name]"
                    @send-image="value => createImageObject(value)" />

                <b-form-group v-if="input.type === 'date'"
                              class="w-100"
                              :required="input.required"
                              :state="formValidated ? !!params[input.name] : null"
                              :invalid-feedback="$t('form.required_field')">
                  <DatePickerInput
                      :is-range="input['is-range']"
                      :is-required="input.required"
                      v-model="params[input.name]"
                      with-time
                      :mode="input.mode"
                      :min-date="params[input.min]"
                      :max-date="params[input.max]"
                      :masks="input.masks"
                      :model-config="input.modelConfig"
                  />
                </b-form-group>


                <money
                    class="rounded w-100"
                    style="box-shadow: none"
                    v-if="input.name === 'price'"
                    v-bind="money"
                    v-model="params.price"
                />
                <div class="invalid-feedback position-absolute mt-0 text-left" style="bottom: -16px">
                  {{$t('errors.error_validation')}}
                </div>
                <i v-if="input.type === 'password'"
                   class="fas position-absolute text-black input-icon"
                   :class="!!inputPasswordVisible ? 'fa-eye' : ' fa-eye-slash'"
                   @click="inputPasswordVisible = !inputPasswordVisible"></i>

              </b-input-group>

              <b-input-group class="w-100 d-flex flex-row align-items-center" v-if="input.type === 'number' && input.name === 'quantity'">
                <b-form-input
                    class="rounded w-50"
                    min="1"
                    v-model="params[input.name]"
                    type="number">
                </b-form-input>
              </b-input-group>

              <b-input-group v-if="input.type === 'range'">
                <b-row>
                  <b-col v-for="item in input.options" :key="item.name">
                    <b-form-input
                        class="rounded w-100"
                        min="1"
                        v-model="params[item.name]"
                        type="number"/>
                  </b-col>
                </b-row>
              </b-input-group>

              <FormFieldRepeater v-if="input.type === 'repeater'" :fields="input.fields"/>

              <multiselect
                  v-if="input.name === 'price'"
                  v-model="params.currency"
                  class="w-50 ml-4"
                  id="currency"
                  label="label"
                  track-by="label"
                  :preselect-first="true"
                  placeholder=""
                  :options="currencyOptions"
                  :searchable="false"
                  :allow-empty="false"
                  :close-on-select="true"
                  @remove="value => {debounceCreateParamsObject('currency', value)}"
                  @input="value => {debounceCreateParamsObject('currency', value)}"
                  select-label=""
                  selected-label=""
                  deselect-label="">
                <template slot="noResult">
                  {{$t('global.no_results')}}
                </template>
              </multiselect>
              <textarea
                  class="rounded w-100"
                  v-if="input.type === 'textarea'"
                  rows="6"
                  v-model="params[input.name]"
              />
              <div
                  class="d-flex align-items-end flex-column"
                  v-if="input.type === 'switch' && !inline">
                <h6 class="text-uppercase font-weight-normal text-right">{{input.label}}</h6>
                <div class="d-flex flex-column align-items-end">
                  <label class="switch switch-3d switch-success">
                    <b-form-checkbox type="checkbox"
                                     class="switch-input"
                                     v-model="params[input.name]"
                                     @input="value => {createParamsObject(input.name, value, input.field)}"
                    />
                    <input type="checkbox"
                           class="switch-input"
                           :checked="params[input.name]"
                    >
                    <span class="switch-slider"></span>
                  </label>
                  <span v-if="input.title && params[input.name]"
                        class="d-inline text-success pr-2">{{input.title}}</span>
                  <span v-if="input.title && !params[input.name]"
                        class="d-inline text-muted pr-2">{{input.titleFalse}}</span>
                </div>
              </div>

              <b-row v-if="input.type === 'switch' && inline" :class="{'no-gutters' : 'no_gutters'}">
                <b-col :md="labelBlockWith">
                  <label :for="input.name" class="pr-0 mb-0">{{input.label}}</label>
                </b-col>
                <b-col class="d-flex align-items-center">
                  <label class="switch switch-3d switch-success mb-0">
                    <b-form-checkbox type="checkbox"
                                     class="switch-input"
                                     v-model="params[input.name]"
                                     @input="value => {createParamsObject(input.name, value, input.field)}"
                    />
                    <input type="checkbox"
                           class="switch-input"
                           :checked="params[input.name]"
                    >
                    <span class="switch-slider"></span>
                  </label>
                  <span v-if="input.title && params[input.name]"
                        class="d-inline text-success pl-2">{{input.title}}</span>
                  <span v-if="input.title && !params[input.name]"
                        class="d-inline text-muted pl-2">{{input.titleFalse}}</span>
                </b-col>
              </b-row>

            </b-col>
          </b-row>
        </b-col>

        <b-col
            v-if="showSubmitButton || showDuplicateButton"
            class="d-flex justify-content-end align-self-start">
          <modal
              @ok="duplicate"
              :confirm="$i18n.t('button.duplicate')"
              @hide="showDuplicateModal = false"
              :showModal="showDuplicateModal">
            <template slot="body">
              <h3 class="text-center">{{ $t('global.are_you_sure') }}</h3>
            </template>
          </modal>
          <b-btn
              v-if="showDuplicateButton"
              @click="showDuplicateModal = true"
              variant="outline-primary"
              class="align-self-end btn-width w-100 input-height ml-2"
          >{{$t('button.duplicate')}}
          </b-btn>
          <b-btn
              v-if="showSubmitButton"
              @click="submit"
              class="align-self-end btn-width w-100 input-height ml-2"
          >{{$t('button.save')}}
          </b-btn>
        </b-col>

      </b-row>

    </b-form>

  </div>
</template>

<script>
  import {Money} from 'v-money'
  import ImageUpload from './imageUpload'
  import FormFieldRepeater from '../forms/fields/formFieldRepeater';
  import Templates from "../../views/templates/Templates";
  import { formatDate } from 'vue2-datepicker/src/utils';
  import DatePickerInput from "../forms/fields/datePickerInput";

  export default {
    name: 'formFields',
    middleware: 'auth',
    props: {
      fields: {
        type: Array,
        require: true
      },
      labelBlockWith: {
        type: Number,
        default: 3
      },
      prefillParams: {
        type: Object
      },
      editing: {
        type: Boolean,
        default: false
      },
      mb: {
        type: Number,
        default: 4
      },
      inline: {
        type: Boolean,
        default: false
      },
      showSubmitButton: {
        type: Boolean,
        default: true
      },
      showDuplicateButton: {
        type: Boolean,
        default: false
      },
      formColumns: {
        type: Number,
        default: 12
      },
      emitSubmitForm: {
        type: Boolean,
        default: false
      },
      clearFields: {
        type: Boolean,
        default: false
      },
    },
    data() {
      return {
        formValidated: false,
        isSelectLoading: {},
        loadingOptions: {},
        params: {},
        userTags: [],
        selectOptions: {},
        inputPasswordVisible: false,
        mailchimp_categories: [],
        money: {
          decimal: '.',
          thousands: ',',
          prefix: '',
          suffix: '',
          precision: 2,
          masked: false
        },
        debounceCreateParamsObject: () => {
        },
        debounceSearchSelectOption: () => {
        },
        showDuplicateModal: false
      }
    },
    components: {
      DatePickerInput,
      Templates,
      Money,
      ImageUpload,
      FormFieldRepeater
    },
    async mounted() {
      this.$store.dispatch('currency/getCurrency');
      if (!!this.prefillParams) {
        this.params = this.prefillParams;
        if (this.prefillParams.currency) {
          this.params.currency = this.formatOption(this.prefillParams.currency, 'label');
        }
      } else {
        this.params.active = true;
      }

      this.getSelectOptions();
      this.debounceCreateParamsObject = _.debounce(this.createParamsObject, 500);
      this.debounceSearchSelectOption = _.debounce(this.searchSelectOption, 500);
    },
    computed: {
      selects() {
        return _.filter(this.fields, input => input.type === 'select' && !input.group);
      },
      currencyOptions() {
        return this.$store.getters['currency/currency']
      }
    },
    methods: {
      submit() {
        this.formValidated = true;
        if (this.$refs.form.checkValidity()) {
            this.$emit('submit', this.params);
        } else {
          console.log('fail')
        }
      },
      duplicate () {
        this.formValidated = true;
        if (this.$refs.form.checkValidity()) {
            this.$emit('duplicate', this.params.id);
        } else {
          console.log('fail')
        }
      },
      changeHighlightedOption(id) {
        const optionsWrapper = document.getElementById(id).parentNode.nextSibling.nextSibling;
        const optionsList = optionsWrapper.getElementsByClassName('multiselect__content')[0];
        const highlightedOption = optionsList.getElementsByClassName('multiselect__option--highlight')[0];
        const activeOption = optionsList.getElementsByClassName('multiselect__option--selected')[0];

        if (activeOption) {

          let top = activeOption.offsetTop - 120;

          optionsWrapper.scrollTo(0, top);

          highlightedOption.classList.remove("multiselect__option--highlight");
          activeOption.classList.add("multiselect__option--highlight");
        }
      },
      showInput(name) {
        if (name === 'team_id' && this.editing) {
          return false
        }
        // if (!!this.params.country && !!this.params.country.name) {
        //   if (name === 'state') {
        //     return this.params.country.name.includes('United States')
        //   } else {
        //     return true
        //   }
        // } else {
        //   return true
        // }
        return true
      },
      addTag(name, value, remove) {
        if (!remove) {
          const tag = {
            name: value,
            value: value,
            code: value
          };
          if (!this.params[name]) {
            this.params[name] = []
          }
          this.params[name].push(tag);
        } else {
          this.params[name] = value
        }
        this.selectOptions[name] = this.params[name];
      },
      createParamsObject(name, value) {
        if (name === 'tags') {
          this.addTag(name, value, true)
        }
        this.$forceUpdate();
      },
      createImageObject(formData) {
        this.$emit('send-image', formData);
      },
      searchSelectOption(url, name, filter_field = null, value = null) {
        if (filter_field) {
          this.$set(this.isSelectLoading, name, true);
          const params = {filter: {}};
          if (filter_field && value) {
            params.filter[filter_field] = value
          }
          try {
            this.getData(url, {params}).then(({data}) => {
              this.selectOptions[name] = data.items.map(option => {
                if (option.title) {
                  return {
                    name: option.title,
                    value: option.title
                  }
                } else {
                  return {
                    name: option.name,
                    value: option.id
                  }
                }
              })
            }).then(() => {
              this.isSelectLoading[name] = false;
              this.$set(this.isSelectLoading, name, false);

              this.$forceUpdate();
            })
          } catch (e) {
            console.log(e);
            this.$set(this.isSelectLoading, name, false);
          } finally {
            this.$forceUpdate();
          }
        }
      },
      getSelectOptions() {
        _.forEach(this.selects, async select => {
          this.selectOptions[select.name] = [];
          if (select.options) {
            this.selectOptions[select.name] = select.options;
          } else if (select.options_url === 'mailchimp') {
            this.isSelectLoading[select.name] = true;
            this.isSelectLoading.state = true;

            try {
              let groups = this.$store.getters['data/mailchimp_interests'];

              if (!Array.isArray(groups)) {
                groups = await this.$store.dispatch('data/fetchMailchimpInterests');
              }

              if (Array.isArray(groups)) {
                groups.forEach((group) => {
                  group.interests = group.interests.map((item) => {
                    return {
                      id: item.id,
                      title: item.title,
                      category_id: group.id,
                      category_title: group.title
                    }
                  })
                });

                this.selectOptions[select.name] = groups;

                this.$forceUpdate();
                this.isSelectLoading[select.name] = false;
                this.isSelectLoading.state = false;
              } else {
                this.removeField(select.name);
              }
            } catch (error) {
              console.log(error);
              this.isSelectLoading[select.name] = false;
              this.isSelectLoading.state = false;
              this.removeField(select.name);
            }
          } else if (select.options_url === 'countries') {
            this.isSelectLoading[select.name] = true;
            this.isSelectLoading.state = true;
            try {
              const {data} = await this.getCountries(select.options_url);
              this.selectOptions[select.name] = data.items.map(option => {
                if (option.states && !!option.states.length) {
                  this.selectOptions.state = option.states.map(state => {
                    return {
                      name: state.title,
                      value: state.title
                    }
                  });
                }
                return {
                  name: option.title,
                  value: option.title
                }
              });
              this.$forceUpdate();
              this.isSelectLoading[select.name] = false;
              this.isSelectLoading.state = false;
            } catch (err) {
              console.log(err);
              this.isSelectLoading[select.name] = false;
              this.isSelectLoading.state = false;
            }
          } else if (select.options_url) {
            this.isSelectLoading[select.name] = true;
            try {
              const {data} = await this.getData(select.options_url);

              if (data && data.items) {
                this.selectOptions[select.name] = data.items.map(option => {
                  if (option.title) {
                    return {
                      name: option.title,
                      value: option.title
                    }
                  } else {
                    return {
                      name: option.name,
                      value: option.id
                    }
                  }
                });
                this.isSelectLoading[select.name] = false;
                this.$forceUpdate();
              }
            } catch (err) {
              console.log(err);
              this.isSelectLoading[select.name] = false;
            }
          }
          this.$forceUpdate();
        })
      },
      removeField(name) {
        if (!name) return;

        let itemIndex = this.fields.findIndex((item) => item.name === name);

        this.fields.splice(itemIndex, 1);
      },
      normalizeDate(value) {
        if(value && value.start && value.end) {
          return formatDate(value.start, "MM/DD/YYYY hh:mm a") + " - " + formatDate(value.end, "MM/DD/YYYY hh:mm a");
        }
        return "MM/DD/YYYY hh:mm - MM/DD/YYYY hh:mm"
      }
    },
    watch: {
      emitSubmitForm() {
        this.submit()
      },
      clearFields() {
        this.formValidated = false;
        // this.params = this.prefillParams;
        if (this.params.currency !== undefined) {
          this.params.currency = {label: "USD", value: "USD"}
        }
      }
    }
  }
</script>
